import { getBuyerStatisticApi, getTurnOverStatisticApi } from "api/statistic";
import { BuyerStatistic, TurnOverStatistics } from "interface/statistic";
import { OrganizationType, UserAccessOrganization } from "interface/user";
import { useEffect, useState, useMemo } from "react";
import { formatNorwegianNumber } from "utils/helper";
import { BuyerGuarantee } from "./BuyerGuarantee";
import ItemCard from "ui/Card";

interface HeaderData {
	title: string;
	subTitle: string;
	valueUnit: string;
	currentValue: string;
	changePercent: number;
	isPercentDecrease: boolean;
}

interface HeaderItem {
	registration?: HeaderData;
	quantityPurchased?: HeaderData;
	amountPurchased?: HeaderData;
	catchAmount?: HeaderData;
	totalPaidOut?: HeaderData;
}

function BuyerCardHeader({
	isMobile,
	year,
	organization,
}: {
	isMobile: boolean;
	year: number;
	organization: UserAccessOrganization | null;
}) {
	const [headerItems, setHeaderItems] = useState<HeaderItem>();

	useEffect(() => {
		const defaultNetWeightTitle = !organization ? "Omsatt kvantum i" : "Kvantum kjøpt i";
		const defaultNetValueTitle = !organization ? "Omsatt verdi i" : "Beløp kjøpt for";

		if (!organization) {
			getTurnOverStatisticApi({ year })
				.then((data) => {
					if (data.data.length === 0) {
						return;
					}
					const turnoverStat = data.data[0];
					setHeaderItems(TurnoverHeader(turnoverStat, year, defaultNetWeightTitle, defaultNetValueTitle));
				})
				.catch((err) => {
					console.log(err);
				});
			return;
		}

		if (organization?.type === OrganizationType.Buyer) {
			getBuyerStatisticApi({
				buyerId: organization.ledgerAccountNumber?.trim() as any,
				year,
			})
				.then((data) => {
					setHeaderItems(
						handleBuyerHeaderItem(organization.type, data.data, year, defaultNetWeightTitle, defaultNetValueTitle),
					);
				})
				.catch((err) => {
					console.log(err);
				});
		}
	}, [organization, year]);

	const itemCardStyle = useMemo(() => ({ width: "100%" }), []);

	if (!headerItems) {
		return <div></div>;
	}

	return (
		<div
			style={{
				display: "flex",
				flexDirection: isMobile ? "column" : "row",
				gap: "30px",
			}}
		>
			<>
				<ItemCard
					title={headerItems?.quantityPurchased?.title as any}
					subTitle={headerItems?.quantityPurchased?.subTitle as any}
					currentValue={headerItems?.quantityPurchased?.currentValue as any}
					valueUnit={headerItems?.quantityPurchased?.valueUnit as any}
					changePercent={headerItems?.quantityPurchased?.changePercent as any}
					isPercentDecrease={
						headerItems?.quantityPurchased?.isPercentDecrease as any
					}
					style={itemCardStyle}
				/>
				<ItemCard
					title={headerItems?.amountPurchased?.title as any}
					subTitle={headerItems?.amountPurchased?.subTitle as any}
					currentValue={headerItems?.amountPurchased?.currentValue as any}
					valueUnit={headerItems?.amountPurchased?.valueUnit as any}
					changePercent={headerItems?.amountPurchased?.changePercent as any}
					isPercentDecrease={
						headerItems?.amountPurchased?.isPercentDecrease as any
					}
					style={itemCardStyle}
				/>
			</>
			{organization?.type === OrganizationType.Buyer && (
				<div style={{ width: "100%" }}>
					<BuyerGuarantee
						availableBalance={headerItems?.totalPaidOut?.currentValue as any}
						balance={headerItems?.amountPurchased?.currentValue as any}
						availableBalancePercentage={
							headerItems?.quantityPurchased?.currentValue as any
						}
					/>
				</div>
			)}
		</div>
	);
}

export default BuyerCardHeader;

const handleBuyerHeaderItem = (
	type: OrganizationType,
	data: BuyerStatistic,
	year: number,
	netWeigthTitle: string,
	netValueTitle: string,
	weightUnit: string = "Kg",
): HeaderItem => {
	const _data: HeaderItem = {};
	const decimalPlaces = 2;
	_data.quantityPurchased = {
		currentValue: formatNorwegianNumber(data.totalLiveWeightKg),
		changePercent: formatNorwegianNumber(
			data.totalLiveWeightKgPercentageDifference,
			decimalPlaces,
			decimalPlaces,
		) as any,
		title: netWeigthTitle + " " + year,
		subTitle: "sammenlignet med samme periode fjoråret",
		isPercentDecrease: data.totalLiveWeightKg < data.lastYearTotalLiveWeightKg,
		valueUnit: weightUnit,
	};
	_data.amountPurchased = {
		currentValue: formatNorwegianNumber(data.totalBuyerAmount) as any,
		changePercent: formatNorwegianNumber(
			data.totalBuyerAmountPercentageDifference,
			decimalPlaces,
			decimalPlaces,
		) as any,
		title: netValueTitle + " " + year,
		subTitle: "sammenlignet med samme periode fjoråret",
		isPercentDecrease: data.totalBuyerAmount < data.lastYearTotalBuyerAmount,
		valueUnit: "Kr",
	};

	return _data;
};

const TurnoverHeader = (turnover: TurnOverStatistics, year: number, netWeigthTitle: string, netWalueTitle: string) => {
	const stat: BuyerStatistic = {
		buyerId: 0,
		totalLiveWeightKg: turnover.totalWeightTonnes,
		lastYearTotalLiveWeightKg: turnover.totalWeightTonnesPreviousYear,
		totalLiveWeightKgPercentageDifference: turnover.weightChangePercent,
		totalBuyerAmount: turnover.totalValueNok,
		lastYearTotalBuyerAmount: turnover.totalValueNokPreviousYear,
		totalBuyerAmountPercentageDifference: turnover.valueChangePercent,
		validGuaranteeAmount: 0,
		balance: 0,
		availableBalance: 0,
		lastUpdated: new Date(),
		availableBalancePercentage: 0,
	};
	const weightUnit = "Tonn";
	return handleBuyerHeaderItem(OrganizationType.Buyer, stat, year, netWeigthTitle, netWalueTitle, weightUnit);
};
